import React, { FunctionComponent } from 'react';
import IntroPopup from '../Popup/IntroPopup';
import { LEFT, TOP } from '../Popup/alignments';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import styles from './ProductMenu.module.less';
import ProductMenuFilter from './ProductMenuFilter';
import ProductSwiper from './ProductSwiper';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFiltersAsArray,
  selectProductMenuFilter,
} from '../../state/productMenu/productMenuSelectors';
import { thunkSetFilter } from '../../state/productMenu/productMenuThunks';
import {
  selectIsMobileOrTablet,
  selectIsMobilePortrait,
  selectIsTabletPortrait,
} from '../../state/userAgent/userAgentSelectors';
import classNames from 'classnames';
import { isFixedRoom } from '../../util/room';
import WallResizerIcon from '../ActionButtons/WallResizerIcon/WallResizerIcon';
import Measurements from '../ActionButtons/Measurements/Measurements';
import {
  selectFilterIntroPopupVisible,
  selectIntroPopupsVisible,
} from '../../state/popups/popupsSelectors';

export interface Props {
  forwardEvent: any;
}

const ProductMenu: FunctionComponent<Props> = ({ forwardEvent }) => {
  const filters = useSelector(selectFiltersAsArray);
  const currentFilter = useSelector(selectProductMenuFilter);
  const introPopupsVisible = useSelector(selectIntroPopupsVisible);
  const displayIntro = !useSelector(selectIsMobileOrTablet);
  const isMobilePortrait = useSelector(selectIsMobilePortrait);
  const isTabletPortrait = useSelector(selectIsTabletPortrait);
  const filterIntroPopupVisible = useSelector(selectFilterIntroPopupVisible);

  const dispatch = useDispatch();

  const setFilter = (filter: string) => dispatch(thunkSetFilter(filter));

  React.useEffect(() => {
    setFilter(currentFilter.name);
  }, []);

  /**
   * Render header label
   * @returns {JSX.Element}
   */
  const renderHeaderLabel = () =>
    isTabletPortrait && (
      <div className={classNames(styles.headerLabel)}>
        {translate(t[currentFilter.labelTranslationKey])}
      </div>
    );

  /**
   * Render action buttons
   * @returns {JSX.Element}
   */
  const renderActionButtons = () =>
    isMobilePortrait && (
      <div className={styles.actionButtons}>
        {isFixedRoom() && <WallResizerIcon />}
        <Measurements />
      </div>
    );

  /**
   * Render filters
   * @returns {unknown[]}
   */
  const renderFilters = () =>
    filters.map(filter => (
      <ProductMenuFilter filter={filter} key={filter.name} />
    ));

  /**
   * Render product Menu filters
   * @returns {JSX.Element}
   */
  const renderProductMenuFilters = () => (
    <div className={classNames(styles.productMenuWrapper)}>
      <IntroPopup
        showPopup={filterIntroPopupVisible}
        alignment={isTabletPortrait ? TOP : LEFT}
        wiggle={true}
        content={translate(t.TOOLTIP_FILTER_HINT)}
        dismissible
      >
        <div className={styles.filterContainer}>
          {renderFilters()}
          {renderHeaderLabel()}
        </div>
      </IntroPopup>
      {renderActionButtons()}
    </div>
  );

  return (
    <IntroPopup
      showPopup={introPopupsVisible && displayIntro}
      alignment={LEFT}
      wiggle={true}
      content={translate(t.BALLOON_HINT_STEP_THREE_UNIT)}
    >
      <div id="product-menu" className={styles.wrapper}>
        {renderProductMenuFilters()}
        <div id="product-swiper" className={styles.swiperWrapper}>
          <ProductSwiper forwardEvent={forwardEvent} />
        </div>
      </div>
    </IntroPopup>
  );
};

export default ProductMenu;
